import { Component, OnInit, ViewChild, ViewChildren, QueryList, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ProjectsRest } from '../projects.rest';
import { LoginRest } from '../../login/login.rest';
import { ProjectDetailsHeaderComponent } from './project-header/project-header.component';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material';
import { Project, ProjectType, ProjectStep, ProjectStepRow } from '../projects.model';
import { AttachmentsComponent } from '../../core/shared/attachments/attachments.component';
import { MenuButton } from '../../core/shared/tabmenu/tabmenu.model';
import { ProjectStepListComponent } from '../projectstep-list/projectstep-list.component';
import { RichTextComponent } from '../../core/shared/richtext/richtext.component';
import { PreviousProjectTypeKey } from '../../core/services/grid-params-restoring/grid-params-restoring.model';
import { SpinnerService } from '../../core/services/spinner-handler/spinner-handler.service';
import { GridParamsRestoringService } from '../../core/services/grid-params-restoring/grid-params-restoring.service';
import { ProjectService } from '../projects.service';
import { HasPermissions, Permissions } from '../../core/services/permissions/permissions.model';
import { PermissionsService } from '../../core/services/permissions/permissions.service';
import { AttachmentRest } from '../../core/shared/attachments/attachments.rest';

@Component({
  selector: 'app-project-details',
  templateUrl: './project-details.component.html'
})
export class ProjectDetailsComponent implements OnInit, AfterViewInit, HasPermissions {
  permissions: Permissions;

  private project: Project;
  private type: ProjectType;
  private descriptionView: RichTextComponent = null;
  private projectId: number;
  private previousProjectType: ProjectType = ProjectType.Project;
  private enabled: boolean
  private projectSteps: ProjectStepRow[];
  private canShowProjectSteps: boolean = false;
  private selectedTabIndex: number = 1;
  private isSaving = false;
  private menuButtons: MenuButton[] = [
    { text: 'COMMONBUTTONS_save', class: "btn-success", icon: '', onClick: () => { this.saveProject(); }, enabled: false, isForSelected: false },
    { text: 'COMMONBUTTONS_close', class: "btn-danger", icon: '', onClick: () => { this.close(); }, enabled: true, isForSelected: false }
  ];;
  private saveButton = { text: 'COMMONBUTTONS_save', class: "btn-success", icon: '', onClick: () => { this.saveProject(); }, enabled: false, isForSelected: false };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private rest: ProjectsRest,
    private loginRest: LoginRest,
    private spinnerService: SpinnerService,
    private gridParamsRestoringService: GridParamsRestoringService,
    private projectService: ProjectService,
    private permissionService: PermissionsService,
    private attachmentRest: AttachmentRest
  ) {
    this.projectDescriptionViews = new QueryList<RichTextComponent>();
  }

  @ViewChild(ProjectDetailsHeaderComponent) projectDetailsHeaderComponent: ProjectDetailsHeaderComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  @ViewChild(ProjectStepListComponent) projectStepListComponent: ProjectStepListComponent;
  @ViewChildren(RichTextComponent) projectDescriptionViews: QueryList<RichTextComponent>;
  @ViewChild('tabGroup') tabGroup: MatTabGroup;

  @HostListener('document:keydown.control.s', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.saveProject();
    event.preventDefault();
  }

  ngOnInit() {
    this.selectedTabIndex = 1;
    this.setPermissions('Project');
    this.canShowProjectSteps = this.permissionService.getUserPermissions("ProjectStep").canShow;
    this.spinnerService.show();
    this.route.queryParams.subscribe(params => {
      let type = parseInt(params.projectType) as ProjectType;
      this.type = type;
      let projectId = parseInt(params.id);
      this.projectId = projectId;
      if (this.projectId === 0)
        this.previousProjectType = this.type;
      this.projectService.currentProject.subscribe((project: Project) => {
        if (!project || project.id !== projectId) {
          this.spinnerService.show();
          this.rest.getProject(projectId, type).subscribe((value: Project) => {
            this.project = value;
            this.enabled = this.permissions.canEdit;
            this.projectDetailsHeaderComponent.setModel(this.project, type);
            this.spinnerService.hide();
            this.menuButtons.find(x => x.text === 'COMMONBUTTONS_save').enabled = this.enabled;
            this.saveButton.enabled = this.enabled;
          });
        }
        else {
          this.project = project;
          this.enabled = this.permissions.canEdit;
          this.spinnerService.show();
          this.projectDetailsHeaderComponent.setModel(this.project, type);
          this.spinnerService.hide();
          this.menuButtons.find(x => x.text === 'COMMONBUTTONS_save').enabled = this.enabled;
          this.saveButton.enabled = this.enabled;
        }
        this.projectService.selectedTab.subscribe((tabIndex: number) => {
          if (tabIndex && tabIndex !== 0) {
            this.selectedTabIndex = tabIndex;
            this.tabIndexChanged(this.selectedTabIndex);
          }
        })
      }).unsubscribe();
    });
    this.getPrevoiusProjectType();
  }

  setPermissions(claimType: string): void {
    this.permissions = this.permissionService.getUserPermissions(claimType);
  }

  ngAfterViewInit() {
    this.spinnerService.hide();
  }

  private getDescription() {
    this.projectDescriptionViews.changes.subscribe((nv: QueryList<RichTextComponent>) => {
      if (!this.descriptionView) {
        this.descriptionView = nv.first;
      }
      if (!this.project.isNew) {
        this.descriptionView.setHtml(this.project.description);
      } else {
        this.descriptionView.setHtml('');
      }
      if (!this.enabled)
        this.descriptionView.disableContol();
    });
  }

  public tabIndexChanged(index: number): void {
    if (!this.project)
      return;
    if (index === 1) {
      this.projectDetailsHeaderComponent.setModel(this.project, this.type);
    }
    else if (index === 2) {
      if (!this.canShowProjectSteps) {
        this.saveDescription();
        return;
      }
      if (this.projectSteps) {
        this.projectStepListComponent.setModel(this.project, this.type, this.projectSteps);
      }
      else {
        this.spinnerService.show();
        if (this.project.newProjectSteps) {
          let projectSteps: ProjectStepRow[] = new Array<ProjectStepRow>();
          this.project.newProjectSteps.forEach((projectStep: ProjectStep) => {
            let projectStepRow = new ProjectStepRow();
            projectStepRow.id = projectStep.id;
            projectStepRow.isPublic = projectStep.isPublic;
            projectStepRow.name = projectStep.name;
            projectStepRow.payerName = projectStep.payerName;
            projectStepRow.startDate = projectStep.startDate;
            projectStepRow.endDate = projectStep.endDate;
            projectStepRow.objectStateName = projectStep.objectStateName;
            projectSteps.push(projectStepRow);
          });
          this.projectSteps = projectSteps;
          this.projectStepListComponent.setModel(this.project, this.type, this.projectSteps);
          this.spinnerService.hide();
        }
        else {
          this.rest.getProjectStepRows(this.project.id).subscribe((projectSteps: ProjectStepRow[]) => {
            this.projectSteps = projectSteps;
            this.projectStepListComponent.setModel(this.project, this.type, this.projectSteps);
            this.spinnerService.hide();
          });
        }
      }
    }
    else if (index === 3) {
      this.getDescription();
      //this.projectDescriptionViews.notifyOnChanges();
    }
    else if (index === 4) {
      if (!this.project.attachments || this.project.attachments.length === 0) {
        let path = `api/projects/attachments/grid/${this.project.objectTypeId}/${this.project.id}`;
        this.attachmentRest.getAttachmentsGrid(path).subscribe(res => {
          this.project.attachments = res;
          this.attachmentsComponent.setModel(this.project, 'projects');
        });
      }
      else {
        this.attachmentsComponent.setModel(this.project, 'projects');
      }
    }
    this.saveDescription();
  }

  public saveDescription() {
    if (this.descriptionView == null) {
      return;
    }
    if (this.project && typeof (this.descriptionView.getHtml()) === "string" && this.descriptionView.getHtml() !== '') {
      this.project.description = this.descriptionView.getHtml();
    }
  }

  private saveProject() {
    if (this.projectDetailsHeaderComponent.isInvalid()) {
      sweetAlert('Dane są niekompletne, nie można zapisać');
      return;
    }
    this.saveDescription();

    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    this.rest.saveProject(this.project).subscribe(id => {
      if (this.projectId === 0) {
        this.gridParamsRestoringService.saveAddingNewRowInfo(id, this.previousProjectType);
      }
      let path = this.getPreviousPath();
      this.router.navigate([path], { relativeTo: this.route });
      this.projectService.complete();
      this.isSaving = false;
    });

  }

  private close() {
    let path = this.getPreviousPath();
    this.router.navigate([path], { relativeTo: this.route });
    this.projectService.complete();
  }

  private getPrevoiusProjectType() {
    let previousProjectTypeStr = sessionStorage.getItem(PreviousProjectTypeKey);
    if (previousProjectTypeStr) {
      let previousProjectType = parseInt(previousProjectTypeStr) as ProjectType;
      this.previousProjectType = previousProjectType;
    }
  }

  private getPreviousPath() {
    switch (this.previousProjectType) {
      case ProjectType.Order:
        return '../../../orders';

      case ProjectType.Service:
        return '../../../services';

      case ProjectType.Consultations:
        return '../../../consultations';

      default:
        return '../../';
    }
  }
}
